import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import {
  FiPlus,
  FiEdit2,
  FiTrash2,
  FiStar,
  FiMapPin,
  FiCheck,
  FiX,
} from "react-icons/fi";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CardSkeleton } from "@/components/common";

type Testimonial = {
  id: string;
  name: string;
  location: string;
  content: string;
  rating: number;
  loanType: string;
  isActive: boolean;
  displayOrder: number;
  createdAt: string;
};

const loanTypes = [
  "First Home Buyer",
  "Refinancing",
  "Investment Property",
  "Construction Loan",
  "Self-Employed",
  "Business Loan",
  "Other",
];

export function TestimonialsPage() {
  const [testimonials, setTestimonials] = useState<Testimonial[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingTestimonial, setEditingTestimonial] = useState<Testimonial | null>(null);
  const [formData, setFormData] = useState({
    name: "",
    location: "",
    content: "",
    rating: 5,
    loanType: "First Home Buyer",
    isActive: true,
    displayOrder: 0,
  });

  const fetchTestimonials = useCallback(async () => {
    try {
      const response = await fetch("/api/admin/testimonials", {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json() as Testimonial[];
        setTestimonials(data);
      }
    } catch (error) {
      console.error("Failed to fetch testimonials:", error);
      toast.error("Failed to load testimonials");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTestimonials();
  }, [fetchTestimonials]);

  const openCreateDialog = () => {
    setEditingTestimonial(null);
    setFormData({
      name: "",
      location: "",
      content: "",
      rating: 5,
      loanType: "First Home Buyer",
      isActive: true,
      displayOrder: testimonials.length + 1,
    });
    setIsDialogOpen(true);
  };

  const openEditDialog = (testimonial: Testimonial) => {
    setEditingTestimonial(testimonial);
    setFormData({
      name: testimonial.name,
      location: testimonial.location,
      content: testimonial.content,
      rating: testimonial.rating,
      loanType: testimonial.loanType,
      isActive: testimonial.isActive,
      displayOrder: testimonial.displayOrder,
    });
    setIsDialogOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      if (editingTestimonial) {
        // Update
        const response = await fetch(`/api/admin/testimonials/${editingTestimonial.id}`, {
          method: "PATCH",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(formData),
        });

        if (response.ok) {
          toast.success("Testimonial updated");
          fetchTestimonials();
          setIsDialogOpen(false);
        } else {
          toast.error("Failed to update testimonial");
        }
      } else {
        // Create
        const response = await fetch("/api/admin/testimonials", {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(formData),
        });

        if (response.ok) {
          toast.success("Testimonial created");
          fetchTestimonials();
          setIsDialogOpen(false);
        } else {
          toast.error("Failed to create testimonial");
        }
      }
    } catch (error) {
      toast.error("An error occurred");
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Are you sure you want to delete this testimonial?")) return;

    try {
      const response = await fetch(`/api/admin/testimonials/${id}`, {
        method: "DELETE",
        credentials: "include",
      });

      if (response.ok) {
        toast.success("Testimonial deleted");
        setTestimonials((prev) => prev.filter((t) => t.id !== id));
      } else {
        toast.error("Failed to delete testimonial");
      }
    } catch (error) {
      toast.error("An error occurred");
    }
  };

  const toggleActive = async (testimonial: Testimonial) => {
    try {
      const response = await fetch(`/api/admin/testimonials/${testimonial.id}`, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ isActive: !testimonial.isActive }),
      });

      if (response.ok) {
        toast.success(testimonial.isActive ? "Testimonial hidden" : "Testimonial visible");
        setTestimonials((prev) =>
          prev.map((t) =>
            t.id === testimonial.id ? { ...t, isActive: !t.isActive } : t
          )
        );
      }
    } catch (error) {
      toast.error("Failed to update testimonial");
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold">Testimonials</h1>
        </div>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {[1, 2, 3].map((i) => (
            <CardSkeleton key={i} />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Testimonials</h1>
          <p className="text-muted-foreground">
            Manage customer testimonials displayed on your website
          </p>
        </div>
        <Button onClick={openCreateDialog} className="gap-2">
          <FiPlus className="size-4" />
          Add Testimonial
        </Button>
      </div>

      {/* Stats */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold">{testimonials.length}</div>
            <p className="text-sm text-muted-foreground">Total Testimonials</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-green-600">
              {testimonials.filter((t) => t.isActive).length}
            </div>
            <p className="text-sm text-muted-foreground">Active</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-muted-foreground">
              {testimonials.filter((t) => !t.isActive).length}
            </div>
            <p className="text-sm text-muted-foreground">Hidden</p>
          </CardContent>
        </Card>
      </div>

      {/* Testimonials Grid */}
      {testimonials.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <FiStar className="size-12 text-muted-foreground/50 mb-4" />
            <h3 className="font-medium text-lg mb-2">No testimonials yet</h3>
            <p className="text-muted-foreground text-center mb-4">
              Add your first customer testimonial to display on your website.
            </p>
            <Button onClick={openCreateDialog} className="gap-2">
              <FiPlus className="size-4" />
              Add Testimonial
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {testimonials.map((testimonial) => (
            <Card
              key={testimonial.id}
              className={testimonial.isActive ? "" : "opacity-60"}
            >
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="text-base">{testimonial.name}</CardTitle>
                    <p className="text-sm text-muted-foreground flex items-center gap-1">
                      <FiMapPin className="size-3" />
                      {testimonial.location}
                    </p>
                  </div>
                  <Badge variant={testimonial.isActive ? "default" : "secondary"}>
                    {testimonial.isActive ? "Active" : "Hidden"}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                {/* Rating */}
                <div className="flex gap-0.5">
                  {[...Array(5)].map((_, i) => (
                    <FiStar
                      key={i}
                      className={`size-4 ${
                        i < testimonial.rating
                          ? "text-amber-400 fill-amber-400"
                          : "text-muted-foreground"
                      }`}
                    />
                  ))}
                </div>

                {/* Content */}
                <p className="text-sm text-muted-foreground line-clamp-3">
                  "{testimonial.content}"
                </p>

                {/* Loan Type */}
                <Badge variant="outline" className="text-xs">
                  {testimonial.loanType}
                </Badge>

                {/* Actions */}
                <div className="flex gap-2 pt-2 border-t">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => toggleActive(testimonial)}
                    className="flex-1"
                  >
                    {testimonial.isActive ? (
                      <>
                        <FiX className="size-4 mr-1" />
                        Hide
                      </>
                    ) : (
                      <>
                        <FiCheck className="size-4 mr-1" />
                        Show
                      </>
                    )}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => openEditDialog(testimonial)}
                  >
                    <FiEdit2 className="size-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDelete(testimonial.id)}
                    className="text-destructive hover:text-destructive"
                  >
                    <FiTrash2 className="size-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {editingTestimonial ? "Edit Testimonial" : "Add Testimonial"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Name</label>
                <Input
                  value={formData.name}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, name: e.target.value }))
                  }
                  placeholder="Sarah & Michael"
                  required
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Location</label>
                <Input
                  value={formData.location}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, location: e.target.value }))
                  }
                  placeholder="Springfield, QLD"
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Testimonial</label>
              <textarea
                value={formData.content}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, content: e.target.value }))
                }
                placeholder="Share the customer's experience..."
                required
                rows={4}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Loan Type</label>
                <Select
                  value={formData.loanType}
                  onValueChange={(value) =>
                    setFormData((prev) => ({ ...prev, loanType: value }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {loanTypes.map((type) => (
                      <SelectItem key={type} value={type}>
                        {type}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Rating</label>
                <Select
                  value={formData.rating.toString()}
                  onValueChange={(value) =>
                    setFormData((prev) => ({ ...prev, rating: parseInt(value) }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[5, 4, 3, 2, 1].map((rating) => (
                      <SelectItem key={rating} value={rating.toString()}>
                        {rating} Star{rating !== 1 ? "s" : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Display Order</label>
                <Input
                  type="number"
                  value={formData.displayOrder}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      displayOrder: parseInt(e.target.value) || 0,
                    }))
                  }
                  min={0}
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Status</label>
                <Select
                  value={formData.isActive ? "active" : "hidden"}
                  onValueChange={(value) =>
                    setFormData((prev) => ({ ...prev, isActive: value === "active" }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="hidden">Hidden</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button type="submit">
                {editingTestimonial ? "Save Changes" : "Add Testimonial"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
